import { createSlice } from "@reduxjs/toolkit";
import {
  fetchPurchaseReturnNumber,
  createPurchaseReturn,
  fetchPurchaseReturnAccs,
  fetchPurchaseReturnRecord,
  fetchSupplierInvoices,
  fetchInvoiceItemsForReturn,
} from "./purchaseReturnThunk";
import { currentDateTime } from "../../../../lib/functions";
import Big from "big.js";

const initialState = {
  supplierInfo: {},
  selectedInvoice: null,
  purId: null,
  invoiceList: [],
  itemCart: [],
  payServiceCart: [],
  purchaseReturnAccs: [],
  otherServiceTrans: [],
  updatedServices: [],
  parentSsccCode: '',
  isTatmeen: false,
  footerValues: {
    subtotal: "0.00",
    totalTax: "0.00",
    grandTotal: "0.00",
    total_discount: "0.00",
    description: "",
    paidAmount: 0,
    dueAmount: 0,
    serviceCharge: 0,
    discountPct: 0,
    taxPct: 0,
    payCart: [],
  },

  tag_field_id: [],
  selectedDiscountAcc: null,
  selectedPurchaseReturnAcc: null,
  selectedTaxAcc: null,
  conversion_rate: 0,
  conversion_currency: null,

  voucherNumber: null,
  pur_r_id: null,
  entryDate: currentDateTime(),
  dueDate: null,
  paymentTerm: "Immediate",

  selectedDetails: null,
  loading: false,
  success: null,
  taxMode: "on_item", // or "on_total"
};

const purchaseReturnSlice = createSlice({
  name: "purchaseReturn",
  initialState,
  reducers: {
    resetPurchaseReturn() {
      return { ...initialState };
    },

    resetPurchaseReturnFields(state) {
      state.selectedDiscountAcc = null;
      state.selectedTaxAcc = null;
      // state.selectedPurchaseReturnAcc = null;
      state.itemCart = [];
      state.payServiceCart = [];
      state.footerValues = { ...initialState.footerValues };
      // state.voucherNumber = null;
      state.exp_id = null;
      state.taxMode = initialState.taxMode; // reset back to default
    },

    setSupplier(state, action) {
      state.supplierInfo = action.payload || {};
    },
    setInvoiceList(state, action) {
      state.invoiceList = action.payload || [];
    },
    setSelectedInvoice(state, action) {
      state.selectedInvoice = action.payload || null;
    },
    setPurIid(state, action) {
      state.purId = action.payload || null;
    },
    setUpdatedService(state, action) {
      state.updatedServices = action.payload || null;
    },

    addOrderRow(state, action) {
      state.itemCart.push(action.payload);
    },
    deleteOrderRow(state, action) {
      state.itemCart = state.itemCart.filter((r) => r.key !== action.payload);
    },
    // updateOrderRow(state, action) {
    //   const { rowKey, updates } = action.payload;
    //   state.itemCart = state.itemCart.map((row) =>
    //     row.key === rowKey ? { ...row, ...updates } : row
    //   );
    // },
    updateOrderRow(state, action) {
      const { rowKey, updates } = action.payload;
      state.itemCart = state.itemCart.map((row) =>
        row.key === rowKey ? {
          ...row,
          ...updates,
          // Ensure child_sscc_code is cleared if is_tatmeen_required is false
          ...(updates.is_tatmeen_required === false ? {
            child_sscc_code: '',
            selectedSerials: []
          } : {})
        } : row
      );
    },

    setPayServiceCart(state, action) {
      state.payServiceCart = action.payload || [];
    },

    setTagFieldId(state, action) {
      state.tagFieldId = action.payload || null;
    },

    setIsTatmeen(state, action) {
      state.isTatmeen = action.payload;
    },

    setParentSsccCode(state, action) {
      state.parentSsccCode = action.payload || '';
    },

    setPurchaseReturnAcc(state, action) {
      state.selectedPurchaseReturnAcc = action.payload;
    },
    setDiscountAcc(state, action) {
      state.selectedDiscountAcc = action.payload;
    },
    setTaxAcc(state, action) {
      state.selectedTaxAcc = action.payload;
    },

    setConversion(state, action) {
      const { rate, currency } = action.payload || {};
      state.conversion_rate = rate || 0;
      state.conversion_currency = currency || null;
    },

    setEntryDate(state, action) {
      state.entryDate = action.payload;
    },
    setTagFeildNameid(state, action) {
      state.tag_field_id = action.payload;
    },
    setDueDate(state, action) {
      state.dueDate = action.payload;
    },

    setTaxMode(state, action) {
      state.taxMode = action.payload;
    },

    setFormField(state, action) {
      const { field, value } = action.payload;

      if (field === "payCart") {
        state.footerValues.payCart = value || [];
      } else {
        state[field] = value;
      }
    },

    setFooterValues(state, action) {
      state.footerValues = { ...state.footerValues, ...action.payload };
    },

    calculateTotals(state, action) {
      const authInfo = action?.payload?.authInfo || {};
      const isIndia = authInfo?.country?.toLowerCase() === "india";
      const gstEnabled = authInfo?.gst_enabled === "yes";
      const showTax = !isIndia || gstEnabled;
      let subtotal = Big(0);
      let totalDiscount = Big(0);
      let totalTax = Big(0);

      state.itemCart.forEach((it) => {
        const qty = Big(it.return_qty || "0");
        const rate = Big(it.item_return_rate || "0");

        if (it.bonus) return;

        const line = qty.times(rate);
        subtotal = subtotal.plus(line);

        if (state.taxMode === "on_item") {
          const discountPct = Big(it.discount || 0);
          const discountAmt = line.times(discountPct).div(100);
          const afterDiscount = line.minus(discountAmt);
          const taxPer = showTax ? Big(it.tax_per || 0) : Big(0);
          const taxAmt = afterDiscount.times(taxPer).div(100);
          totalDiscount = totalDiscount.plus(discountAmt);
          totalTax = totalTax.plus(taxAmt);
          subtotal = subtotal.plus(taxAmt);
        }
      });

      if (state.taxMode === "on_total") {
        const discountPct = Big(state.footerValues.discountPct || 0);
        totalDiscount = subtotal.times(discountPct).div(100);
        const afterDiscount = subtotal.minus(totalDiscount);
        const taxPct = showTax ? Big(authInfo?.item_tax || 0) : Big(0);
        totalTax = afterDiscount.times(taxPct).div(100);
        state.footerValues.taxPct = taxPct.toNumber();
      }

      const serviceCharge = Big(state.footerValues.serviceCharge || 0);
      const paidAmount = Big(state.footerValues.paidAmount || 0);
      const afterDiscount = subtotal.minus(totalDiscount);
      const grandTotal = state.taxMode === "on_item" ?
        subtotal.plus(serviceCharge) : afterDiscount.plus(totalTax).plus(serviceCharge);

      state.footerValues = {
        ...state.footerValues,
        subtotal: subtotal.toString(),
        // total_discount: totalDiscount.toFixed(2),
        totalTax: totalTax.toString(),
        grandTotal: grandTotal.toString(),
        dueAmount: grandTotal.minus(paidAmount).toString(),
      };
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchPurchaseReturnNumber.fulfilled, (state, action) => {
        state.voucherNumber = action.payload;
      })
      .addCase(fetchSupplierInvoices.fulfilled, (state, action) => {
        state.invoiceList = action.payload || [];
      })
      .addCase(fetchPurchaseReturnAccs.fulfilled, (state, action) => {
        state.purchaseReturnAccs = action.payload;
      })
      .addCase(fetchInvoiceItemsForReturn.fulfilled, (state, action) => {
        const { supplier, itemCart, masterData, otherServiceTrans, parentSsccCode } =
          action.payload || {};

        if (!masterData) return;

        // state.parentSsccCode = parentSsccCode || masterData.sscc_code || '';
        state.parentSsccCode = parentSsccCode || '';
        state.isTatmeen = masterData.is_tatmeen_product === 1 ||
          masterData.is_tatmeen === 1 ||
          false;
        // supplier info
        state.supplierInfo = supplier;

        // item cart
        // state.itemCart = itemCart;
        state.itemCart = itemCart.map(item => ({
          ...item,
         
          child_sscc_code: item.child_sscc_code || '',
          selectedSerials: item.selectedSerials || [],
          serial_mode: item.serial_mode || 'unit',
        }));
        state.otherServiceTrans = otherServiceTrans;
        // master-level info
        state.footerValues = {
          ...state.footerValues,
          narration: masterData.narration || "",
          // subtotal: masterData.sub_total,
          total_discount: masterData.total_discount,
          total_discount_per: masterData.total_discount_per,
          total_tax: masterData.total_tax,
          total_tax_per: masterData.total_tax_per,
          // grandTotal: masterData.total_amount,
          // paidAmount: masterData.paid_amount ?? 0,
          // dueAmount: masterData.due_amount ?? 0,
          discount_acc_id: masterData.discount_acc_id,
          tax_acc_id: masterData.tax_acc_id,
          transport_acc_id: masterData.transport_acc_id,
        };

        state.selectedInvoice = masterData.pur_voucher_no; // original purchase voucher
        // state.entryDate = masterData.created_date;
        // state.taxMode =
        //   masterData.is_cal_type === "individual" ? "individual" : "on_total";
        state.taxMode =
          masterData.is_cal_type === "individual" ? "on_item" : "on_total";
      })
      .addCase(fetchPurchaseReturnRecord.fulfilled, (state, action) => {
        const { record, itemCart, trans, parentSsccCode, isTatmeen } = action.payload || {};
        if (!record) return;

        // ✅ Set Parent SSCC (editable)
        state.parentSsccCode = parentSsccCode || record.sscc_code || record.original_sscc || '';

        // ✅ Set isTatmeen
        state.isTatmeen = isTatmeen || record.is_tatmeen_product === 1 || record.is_tatmeen === 1 || false;

        // 🔑 SET SELECTED INVOICE FOR EDIT MODE
        state.selectedInvoice = {
          pur_id: record.pur_id,
          label: record.selected_invoice_no,   // what user sees
          pur_voucher_no: record.selected_invoice_no,
        };

        state.purId = record.pur_id;

        // supplier
        state.supplierInfo = {
          acc_id: record.acc_id,
          acc_name: record.acc_name,
          institution_name: record.institution_name,
          contact_no: record.contact_no,
          address: record.address,
        };

        // accounts
        state.selectedDiscountAcc = {
          acc_id: record.discount_acc_id,
          acc_name: record.discount_acc_name,
        };
        state.selectedTaxAcc = {
          acc_id: record.tax_acc_id,
          acc_name: record.tax_acc_name,
        };
        state.selectedPurchaseReturnAcc = {
          acc_id: record.purchase_return_acc_id,
          acc_name: record.purchase_return_acc_name,
        };

        // items
        // state.itemCart = itemCart;
        state.itemCart = itemCart.map(item => ({
          ...item,
          // 🔥 Load is_tatmeen_required from API if saved, otherwise default false
          is_tatmeen_required: item.is_tatmeen_required === 1 ||
            item.is_tatmeen_required === "1" ||
            item.is_tatmeen_required === true ||
            false,
          // Ensure these fields exist
          child_sscc_code: item.child_sscc_code || '',
          selectedSerials: item.selectedSerials || [],
          serial_mode: item.serial_mode || 'unit',
        }));

        // ✅ Build payCart first
        const mappedPayCart = (trans || []).map((t, idx) => {
          const isCheque = !!t.cheque_number;

          return {
            key: t.to_acc_id || `pay_${idx}`,
            type: t.to_acc_name || t.acc_name,
            to_acc_id: t.to_acc_id,
            to_acc_name: t.to_acc_name || t.acc_name,
            acc_type_id: isCheque ? "bank" : "cash",
            tran_amount: Number(t.tran_amount || t.amount || 0),
            cheque_number: isCheque ? t.cheque_number : "",
            date: t.created_date || t.cheque_date || state.created_date
          };
        });

        // footer / master
        state.footerValues = {
          ...state.footerValues,
          payCart: mappedPayCart,
          description: record.narration || "",
          subtotal: record.sub_total,
          total_discount: record.total_discount,
          total_discount_per: record.total_discount_per,
          totalTax: record.total_tax,
          total_tax_per: record.total_tax_per,
          grandTotal: record.total_amount,
          paidAmount: record.paid_amount ?? 0,
          dueAmount: record.due_amount ?? 0,
          discountPct: record.total_discount_per ?? 0,
        };

        state.voucherNumber = record.pur_r_voucher_no;
        state.exp_id = record.pur_r_id;
        state.entryDate = record.created_date;
        state.taxMode =
          record.is_cal_type === "individual" ? "on_item" : "on_total";
      })

      .addCase(createPurchaseReturn.pending, (state) => {
        state.loading = true;
      })
      .addCase(createPurchaseReturn.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload;
      })
      .addCase(createPurchaseReturn.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const {
  resetPurchaseReturn,
  setSupplier,
  setInvoiceList,
  setSelectedInvoice,
  addOrderRow,
  deleteOrderRow,
  updateOrderRow,
  calculateTotals,
  setPayServiceCart,
  setTagFieldId,
  setDiscountAcc,
  setTaxAcc,
  setConversion,
  setEntryDate,
  setDueDate,
  setFormField,
  setFooterValues,
  setTaxMode,
  setPurchaseReturnAcc,
  resetPurchaseReturnFields,
  setTagFeildNameid,
  setPurIid,
  setUpdatedService,
  setIsTatmeen,
  setParentSsccCode
} = purchaseReturnSlice.actions;

export default purchaseReturnSlice.reducer;
